import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef, EventEmitter,
  inject,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {BuilderTourEnum} from "../../builder-tour.enum";
import {User} from "@sentry/browser";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {BuilderSectionsInteractionService} from "../../builder-sections-interaction.service";
import {BuilderGeneratedPageI, PageMetaI} from "../../builder-v2.interface";
import {CanvasZoomService} from "../../services/canvas-zoom.service";
import {TriggersService} from "../../services/triggers.service";
import {pairwise} from "rxjs";
import {filter, take} from "rxjs/operators";
import {CommonBuilderDataService} from "../../services/common-builder-data.service";
import {Website} from "../../../websites/website/website";

@Component({
    selector: 'db-builder-outline',
    templateUrl: './builder-outline.component.html',
    styleUrl: './builder-outline.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CanvasZoomService],
    standalone: false
})
export class BuilderOutlineComponent implements OnInit {
  get scaleInPercentage(): number {
    return this._scaleInPercentage;
  }

  set scaleInPercentage(value: number) {
    this._scaleInPercentage = value;
  }

  tourEnum = BuilderTourEnum;
  showCustomization: boolean = false;
  builderInteractionsService = inject(BuilderSectionsInteractionService);
  canvasZoomService = inject(CanvasZoomService);
  _destroyRef = inject(DestroyRef);
  _triggerService = inject(TriggersService);
  @Input() popupMode: boolean = false;
  @Input() website: Website;
  @Input() singlePage: boolean = false;
  @Input() user: User;
  @Input() tourStep: number;
  @Input() uniqueId: string;
  @Input() isMobile: boolean;
  @Input() disableDraggingEvents?: boolean = false;
  @Input() outline: BuilderGeneratedPageI;
  @Input() pageMetas: PageMetaI[];
  coverSteps: number[] = [BuilderTourEnum.OUTLINE_POPUP, BuilderTourEnum.PAGE_BUILDER, BuilderTourEnum.CUSTOMIZER, BuilderTourEnum.GENERATION];
  isSmoothTransition: boolean;
  accentZIndex = 20;
  private _scaleInPercentage: number = this.canvasZoomService.scale.value * 100;
  @Output() onGenerate: EventEmitter<void> = new EventEmitter();
  @Output() openMagicLink: EventEmitter<boolean> = new EventEmitter<boolean>();
  prefilledAi = inject(CommonBuilderDataService).prefilledAi;
  loading = this.builderInteractionsService.loading;
  startAnimations$ = this._triggerService.startSidebarAnimations;
  _prepareDialogWasTriggered: boolean = false;

  ngOnInit() {
    this._listenSectionsLoaded();
    this._listenDiscoverBusinessDataReady();
    this.isMobile && this._listenSectionsLoading();
  }

  private _listenDiscoverBusinessDataReady() {
    this.builderInteractionsService.discoverBusinessDataReady
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        if (value) {
          this._prepareDialogWasTriggered = true;
        }
      });
  }

  private _listenSectionsLoaded() {
    this.builderInteractionsService.secondaryPagesLoaded
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        if (!value) {
          return;
        }
        this._triggerService.calculateMinZoom.next();
        if (this.singlePage) {
          this.builderInteractionsService.showSectionsLoading.next(false);
        }
        this._triggerService.fullyScaleIn.next();
        this._triggerService.startSidebarAnimations.next(true);
        !!this.isMobile && this._triggerService.fitToScreen.next();
      });
  }

  private _listenSectionsLoading() {
    this.builderInteractionsService.secondaryPageLoading
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        pairwise(), // Emit the previous and current values as an array
        filter(([prev, curr]) => prev === true && curr === false), // Check for transition from true to false
        take(1) // Stop after catching the second false
      )
      .subscribe((data) => {
        this.onGenerate.emit();
      });
  }

}
